import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Props interface for the useDynamicCursor hook
 */
interface UseDynamicCursorProps {
  /** Size of the brush in canvas coordinates */
  brushSize: number
  /** Reference to the canvas element where the cursor will be displayed */
  canvasRef: React.RefObject<HTMLCanvasElement>
  /** Whether the dynamic cursor functionality is disabled */
  isDisabled: boolean
  /**
   * Read on every pointer move: a CSS cursor to show instead of the brush circle at that point (e.g. a pointer over a
   * selectable object), or nothing for the brush. A getter, so it answers for the same move that changed it.
   */
  getCursorOverride?: () => string | undefined
}

/**
 * Custom hook that creates a dynamic circular cursor overlay for canvas-based drawing tools.
 * 
 * This hook manages a visual cursor that follows the mouse within the canvas bounds,
 * displaying a circular overlay that represents the brush size. The cursor size is
 * calculated based on the canvas dimensions, brush size to ensure
 * accurate visual representation of the drawing tool.
 * 
 * Key features:
 * - Creates a circular overlay element that follows mouse movement
 * - Handles canvas resize events to recalculate cursor dimensions
 * - Manages cursor visibility based on mouse position relative to canvas
 * - Preserves original cursor style and restores it when disabled
 * - Uses z-index layering to appear above canvas but below modals
 */
export const useDynamicCursor = ({ brushSize, canvasRef, isDisabled, getCursorOverride }: UseDynamicCursorProps) => {
  const getCursorOverrideRef = useRef(getCursorOverride)
  getCursorOverrideRef.current = getCursorOverride
  // Reference to the overlay DOM element that represents the cursor
  const overlayRef = useRef<HTMLDivElement | null>(null)
  // Stores the original cursor CSS style to restore when disabled
  const prevCursorCSSRef = useRef<string | null>(null)
  // State to trigger recalculation when window is resized
  const [recalcTick, setRecalcTick] = useState(0)

  // Function to check if cursor should be visible
  const shouldShowCursor = useCallback((e: PointerEvent, canvasRect: DOMRect) => {
    // Check if pointer is within canvas bounds
    const inside =
      e.clientX >= canvasRect.left - 2 && e.clientX <= canvasRect.right + 2 &&
      e.clientY >= canvasRect.top - 2 && e.clientY <= canvasRect.bottom + 2

    if (!inside) return false

    // Check if there are any elements on top of the canvas at this position
    const elementAtPoint = document.elementFromPoint(e.clientX, e.clientY)
    if (!elementAtPoint) return false

    // Check if the element at the pointer position is the canvas or a child of the canvas
    if (!canvasRef) return false // Guard against undefined canvasRef
    const canvasEl = canvasRef.current
    if (!canvasEl) return false

    // If the element at the pointer is the canvas itself, show cursor
    if (elementAtPoint === canvasEl) return true

    // If the element at the pointer is a child of the canvas, show cursor
    if (canvasEl.contains(elementAtPoint)) return true

    // If there's any other element on top, don't show cursor
    return false
  }, [canvasRef])

  // Effect to handle window resize events and trigger cursor recalculation
  useEffect(() => {
    const onResize = () => setRecalcTick(t => t + 1)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Main effect that handles cursor creation, sizing, and event listeners
  useEffect(() => {
    if (!canvasRef) return // Guard against undefined canvasRef
    const canvasEl = canvasRef.current
    if (!canvasEl) return

    // If disabled, clean up overlay and restore original cursor
    if (isDisabled) {
      if (overlayRef.current && overlayRef.current.parentNode) {
        overlayRef.current.parentNode.removeChild(overlayRef.current)
        overlayRef.current = null
      }
      if (prevCursorCSSRef.current != null) {
        canvasEl.style.cursor = prevCursorCSSRef.current
        prevCursorCSSRef.current = null
      }
      return
    }

    // Store original cursor style if not already stored
    if (prevCursorCSSRef.current == null) {
      prevCursorCSSRef.current = canvasEl.style.cursor
    }

    // Get canvas layout dimensions (clientWidth/Height)
    const clientW_layout = Math.max(1, canvasEl.clientWidth)
    const clientH_layout = Math.max(1, canvasEl.clientHeight)

    // Calculate scaling factors between canvas internal size and display size
    const scaleX = canvasEl.width / clientW_layout
    const scaleY = canvasEl.height / clientH_layout
    const scaleFactor = Math.max(scaleX, scaleY)

    // Calculate base CSS diameter based on brush size and scaling
    const baseCssDiameter = brushSize * (scaleFactor / Math.max(0.000001, scaleX))

    // Get visual dimensions (actual rendered size) from getBoundingClientRect
    const rect = canvasEl.getBoundingClientRect()
    const clientW_visual = Math.max(1, rect.width)
    const clientH_visual = Math.max(1, rect.height)
    const transformScaleX = clientW_visual / clientW_layout
    const transformScaleY = clientH_visual / clientH_layout
    const transformScale = Math.max(transformScaleX, transformScaleY)

    // Calculate final cursor size with transform scaling
    const desiredCssSize = baseCssDiameter * transformScale
    // Apply consistent offset to make cursor slightly smaller than actual brush size
    const sizeOffset = brushSize === 40 ? 6 : 2
    const cssSize = Math.max(
      2, //  Minimum size of 2px to avoid page crashing on negative values
      Math.round(
        Number.isFinite(desiredCssSize) && desiredCssSize > 0
          ? desiredCssSize - sizeOffset
          : baseCssDiameter - sizeOffset
      )
    )

    // Create overlay element if it doesn't exist
    if (!overlayRef.current) {
      const el = document.createElement('div')
      overlayRef.current = el
      // Position fixed to follow mouse cursor
      el.style.position = 'fixed'
      el.style.left = '0px'
      el.style.top = '0px'
      el.style.width = '0px'
      el.style.height = '0px'
      // Disable pointer events so it doesn't interfere with canvas interactions
      el.style.pointerEvents = 'none'
      // Lower z-index to appear below modals but above canvas
      el.style.zIndex = '1000'
      el.style.boxSizing = 'border-box'
      // Circular cursor styling with white border and semi-transparent background
      el.style.border = '1px solid rgba(255,255,255,1)'
      el.style.borderRadius = '50%'
      el.style.background = 'rgba(0,0,0,0.4)'
      el.style.filter = 'drop-shadow(0 0 1px rgba(0,0,0,0.7))'
      el.style.display = 'none' // Initially hidden
      document.body.appendChild(el)
    }

    // Update overlay size and initial state
    overlayRef.current.style.width = `${cssSize}px`
    overlayRef.current.style.height = `${cssSize}px`
    overlayRef.current.style.display = 'none' // Keep hidden until mouse moves
    overlayRef.current.style.visibility = 'hidden'

    // Pointer move handler to position the cursor overlay
    const move = (e: PointerEvent) => {
      const r = canvasEl.getBoundingClientRect()

      const cursorOverride = getCursorOverrideRef.current?.()
      if (cursorOverride && shouldShowCursor(e, r)) {
        // Another cursor here (e.g. over a selectable object): no brush circle
        overlayRef.current!.style.display = 'none'
        overlayRef.current!.style.visibility = 'hidden'
        canvasEl.style.cursor = cursorOverride
      } else if (shouldShowCursor(e, r)) {
        // Show dynamic cursor and hide default cursor
        overlayRef.current!.style.display = 'block'
        overlayRef.current!.style.transform = `translate(${e.clientX - cssSize / 2}px, ${e.clientY - cssSize / 2}px)`
        overlayRef.current!.style.visibility = 'visible'
        canvasEl.style.cursor = 'none'
      } else {
        // Hide dynamic cursor and restore default cursor
        overlayRef.current!.style.display = 'none'
        overlayRef.current!.style.visibility = 'hidden'
        canvasEl.style.cursor = prevCursorCSSRef.current || 'default'
      }
    }

    // Show/hide handlers for pointer enter/leave events
    const show = () => {
      if (overlayRef.current) {
        overlayRef.current.style.display = 'block'
        overlayRef.current.style.visibility = 'visible'
      }
      canvasEl.style.cursor = 'none'
    }
    const hide = () => {
      if (overlayRef.current) {
        overlayRef.current.style.display = 'none'
        overlayRef.current.style.visibility = 'hidden'
      }
      canvasEl.style.cursor = prevCursorCSSRef.current || 'default'
    }

    // Add event listeners for pointer tracking
    window.addEventListener('pointermove', move, { passive: true })
    canvasEl.addEventListener('pointerenter', show, { passive: true })
    canvasEl.addEventListener('pointerleave', hide, { passive: true })

    return () => {
      window.removeEventListener('pointermove', move)
      canvasEl.removeEventListener('pointerenter', show)
      canvasEl.removeEventListener('pointerleave', hide)
    }
  }, [brushSize, canvasRef, isDisabled, recalcTick, shouldShowCursor])

  useEffect(() => {
    if (!canvasRef) return // Guard against undefined canvasRef
    const canvasEl = canvasRef.current
    return () => {
      // Remove overlay element from DOM
      if (overlayRef.current && overlayRef.current.parentNode) {
        overlayRef.current.parentNode.removeChild(overlayRef.current)
        overlayRef.current = null
      }
      // Restore original cursor style
      if (canvasEl && prevCursorCSSRef.current != null) {
        canvasEl.style.cursor = prevCursorCSSRef.current
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
